import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime, PROPERTY_TYPES, REGIONS, STAGE_DISPLAY_NAMES } from '../lib/utils';
import OpportunityTimeline from '../components/OpportunityTimeline';
import { CallClarityBrief, VisionProposalGenerator, OpportunityMomentum, SignalStoryCard, OutreachEmailComposer } from '../components/sales-intelligence';
import OpportunityMediaLauncher from '../components/media-studio/OpportunityMediaLauncher';
import EngagementsSection from '../components/EngagementsSection';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  DollarSign, 
  Percent, 
  MapPin,
  FileText,
  MessageSquare,
  Upload,
  Trash2,
  Edit2,
  Save,
  X,
  Clock,
  Loader2,
  ExternalLink,
  UserPlus,
  Plus,
  TrendingUp,
  Zap,
  Target,
  Calendar,
  Users,
  BarChart3,
  Brain,
  RefreshCw,
  Search,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Download,
  Send
} from 'lucide-react';
import TrackVisitDialog from '../components/mileage/TrackVisitDialog';

const STAGES = [
  { id: 'lead_captured', name: 'Lead Captured' },
  { id: 'intake_submitted', name: 'Intake Submitted' },
  { id: 'discovery_call_completed', name: 'Discovery Call Completed' },
  { id: 'design_deck_in_development', name: 'Design Deck In Development' },
  { id: 'design_deck_delivered', name: 'Design Deck Delivered' },
  { id: 'client_reviewing', name: 'Client Reviewing' },
  { id: 'revision_phase', name: 'Revision Phase' },
  { id: 'approved', name: 'Approved' },
  { id: 'production_vendor_alignment', name: 'Production / Vendor Alignment' },
  { id: 'closed_won', name: 'Closed – Won' },
  { id: 'closed_lost', name: 'Closed – Lost' },
];

const ACTIVITY_TYPES = [
  { id: 'note', name: 'Note', icon: FileText },
  { id: 'call', name: 'Call', icon: Phone },
  { id: 'email', name: 'Email', icon: Mail },
  { id: 'meeting', name: 'Meeting', icon: User },
];

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isExecutiveViewer } = useAuth();
  const { get, put, post, del, uploadFile, loading } = useApi();

  const [opportunity, setOpportunity] = useState(null);
  const [activities, setActivities] = useState([]);
  const [intelligence, setIntelligence] = useState(null);
  const [suggestedContacts, setSuggestedContacts] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [newActivity, setNewActivity] = useState({ type: 'note', content: '' });
  const [businessEnrichment, setBusinessEnrichment] = useState(null); // Business phone/website from enrichment
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  
  // Executive Brief state
  const [briefModalOpen, setBriefModalOpen] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefPdfGenerating, setBriefPdfGenerating] = useState(false);
  const [briefPdfUrl, setBriefPdfUrl] = useState(null);
  const [sendBriefOpen, setSendBriefOpen] = useState(false);
  const [sendingBrief, setSendingBrief] = useState(false);
  const [briefEmailTo, setBriefEmailTo] = useState('');

  // Mileage / Track Visit — only available when the opportunity has a
  // linked contact (primary_contact_id). Logs against the contact record.
  const [trackVisitOpen, setTrackVisitOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [oppData, activityData] = await Promise.all([
        get(`/opportunities/${id}`),
        get(`/activities/${id}`)
      ]);
      setOpportunity(oppData);
      setFormData(oppData);
      setActivities(activityData);
      
      // Fetch deal intelligence
      try {
        const intelData = await get(`/opportunities/${id}/intelligence`);
        setIntelligence(intelData);
      } catch (e) {
        console.error('Failed to load intelligence:', e);
      }
      
      // Fetch suggested contacts
      try {
        const contacts = await get(`/intelligence/suggested-contacts?opportunity_id=${id}`);
        setSuggestedContacts(contacts || []);
      } catch (e) {
        console.error('Failed to load suggested contacts:', e);
      }
      
      // Fetch business enrichment data (phone, website from Google Places)
      try {
        const enrichment = await get(`/opportunities/${id}/business-enrichment`);
        setBusinessEnrichment(enrichment);
      } catch (e) {
        console.log('No business enrichment data:', e);
      }
    } catch (error) {
      toast.error('Failed to load opportunity');
      navigate('/pipeline');
    }
  };

  const runDiscovery = async () => {
    setDiscoveryLoading(true);
    try {
      // Use the new SerpAPI-powered discovery endpoint
      const result = await post(`/intelligence/auto-discover-for-opportunity/${id}`);
      if (result.success) {
        toast.success(`Discovered ${result.total_discovered} decision makers with SerpAPI!`);
        // Show primary target if found
        if (result.primary_outreach_target) {
          toast.info(`Primary target: ${result.primary_outreach_target.name} - ${result.primary_outreach_target.title}`);
        }
        // Refresh suggested contacts
        const contacts = await get(`/intelligence/suggested-contacts?opportunity_id=${id}`);
        setSuggestedContacts(contacts || []);
        // Refresh intelligence (DM count affects it)
        const intelData = await get(`/opportunities/${id}/intelligence`);
        setIntelligence(intelData);
      } else {
        toast.info(result.message || 'No new decision makers found');
      }
    } catch (error) {
      toast.error('Discovery failed: ' + (error.message || 'Unknown error'));
    } finally {
      setDiscoveryLoading(false);
    }
  };

  // Enrich company with Google Places to get phone/website
  const enrichCompany = async () => {
    if (!opportunity?.company_name) {
      toast.error('No company name to enrich');
      return;
    }
    
    setEnrichmentLoading(true);
    try {
      toast.info('Looking up business info...');
      
      // Call the direct company enrichment endpoint
      const result = await post(`/opportunities/${id}/enrich-company`);
      
      if (result.enrichment_status === 'found' || result.enrichment_status === 'found_via_google') {
        setBusinessEnrichment(result);
        toast.success('Business info found!');
        
        // Show what was found
        if (result.business_phone) {
          toast.info(`Phone: ${result.business_phone}`);
        }
        if (result.business_website) {
          toast.info(`Website: ${result.business_website}`);
        }
      } else if (result.enrichment_status === 'not_found') {
        toast.info('No business listing found for this company');
      } else {
        toast.info('Enrichment completed');
      }
    } catch (error) {
      toast.error('Failed to enrich: ' + (error.message || 'Unknown error'));
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const convertContact = async (contactId, contact) => {
    try {
      // Check for existing contact first
      const existingContacts = await get(`/contacts?search=${encodeURIComponent(contact.name)}`);
      const duplicate = existingContacts?.find(c => 
        c.name?.toLowerCase() === contact.name?.toLowerCase() && 
        c.company?.toLowerCase() === contact.company_name?.toLowerCase()
      );
      
      if (duplicate) {
        // Link existing contact instead
        await post(`/opportunities/${id}/contacts`, { contact_id: duplicate.id });
        toast.success('Linked existing contact to opportunity');
      } else {
        // Split name into first and last
        const nameParts = (contact.name || '').trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Try Apollo enrichment first if contact doesn't have email/phone
        let enrichedEmail = contact.email || '';
        let enrichedPhone = contact.phone || '';
        let enrichedTitle = contact.title || '';
        let enrichedLinkedIn = contact.linkedin_url || contact.source_url || '';
        
        if (!enrichedEmail && !enrichedPhone && contact.company) {
          try {
            toast.info('Enriching contact with Apollo...');
            const enrichResult = await post(`/lead-intelligence/contacts/enrich`, {
              first_name: firstName,
              last_name: lastName,
              organization_name: contact.company || opportunity?.company_name || '',
              linkedin_url: enrichedLinkedIn
            });
            
            if (enrichResult.enrichment_data) {
              const data = enrichResult.enrichment_data;
              if (data.email) enrichedEmail = data.email;
              if (data.phone) enrichedPhone = data.phone;
              if (data.title && !enrichedTitle) enrichedTitle = data.title;
              if (data.linkedin_url && !enrichedLinkedIn) enrichedLinkedIn = data.linkedin_url;
              
              if (data.email || data.phone) {
                toast.success('Contact enriched with Apollo!');
              }
            }
          } catch (enrichError) {
            console.log('Apollo enrichment not available:', enrichError);
            // Continue without enrichment
          }
        }
        
        // Use opportunity id as account_id if no account_id exists
        const accountId = opportunity?.account_id || id;
        const newContact = await post(`/contacts`, {
          first_name: firstName,
          last_name: lastName,
          title: enrichedTitle,
          email: enrichedEmail,
          phone: enrichedPhone,
          account_id: accountId,
          linkedin_url: enrichedLinkedIn,
          contact_type_tags: contact.decision_maker_score >= 70 ? ['decision_maker'] : ['influencer'],
          consent_status: 'unknown'
        });
        // Link to opportunity
        await post(`/opportunities/${id}/contacts`, { contact_id: newContact.id });
        toast.success('Contact added to CRM');
      }
      
      // Update suggested contact status
      await put(`/intelligence/suggested-contacts/${contactId}`, { status: 'converted' });
      
      // Refresh
      const contacts = await get(`/intelligence/suggested-contacts?opportunity_id=${id}`);
      setSuggestedContacts(contacts || []);
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : (error?.message || error?.detail || 'Unknown error');
      toast.error('Failed to add contact: ' + errorMsg);
    }
  };

  const dismissContact = async (contactId) => {
    try {
      await del(`/intelligence/suggested-contacts/${contactId}`);
      setSuggestedContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Contact dismissed');
    } catch (error) {
      toast.error('Failed to dismiss contact');
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleSave = async () => {
    try {
      await put(`/opportunities/${id}`, {
        company_name: formData.company_name,
        contact_name: formData.contact_name || null,
        contact_title: (formData.contact_title || '').trim() || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        property_type: formData.property_type || null,
        deal_value: parseFloat(formData.deal_value) || 0,
        probability: parseInt(formData.probability) || 10,
        region: formData.region || null,
        description: formData.description || null,
        stage: formData.stage,
        // Timing context fields
        expected_close_month: formData.expected_close_month || null,
        target_rollout_season: formData.target_rollout_season || null,
        budget_cycle: formData.budget_cycle || null,
      });
      toast.success('Opportunity updated');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Default probabilities by stage
  const DEFAULT_STAGE_PROBABILITY = {
    prospecting: 10,
    strategic_discovery: 20,
    executive_deck_presented: 40,
    refinement: 60,
    production_alignment: 75,
    closed_won: 100,
    closed_lost: 0,
  };

  const handleStageChange = async (newStage) => {
    try {
      const newProbability = DEFAULT_STAGE_PROBABILITY[newStage];
      await put(`/opportunities/${id}`, { 
        stage: newStage,
        probability: newProbability 
      });
      toast.success(`Stage updated to ${STAGE_DISPLAY_NAMES[newStage]} (${newProbability}% probability)`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!newActivity.content.trim()) return;

    try {
      await post('/activities', {
        opportunity_id: id,
        activity_type: newActivity.type,
        content: newActivity.content,
      });
      toast.success('Activity added');
      setNewActivity({ type: 'note', content: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadFile(`/upload/${id}`, file);
      toast.success('File uploaded');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      await del(`/opportunities/${id}`);
      toast.success('Opportunity deleted');
      navigate('/pipeline');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Executive Brief handlers
  const openExecutiveBrief = async () => {
    setBriefModalOpen(true);
    setBriefLoading(true);
    setBrief(null);
    setBriefPdfUrl(null);
    try {
      const data = await get(`/opportunities/${id}/executive-brief`);
      setBrief(data);
    } catch (error) {
      toast.error('Failed to generate executive brief');
      setBriefModalOpen(false);
    } finally {
      setBriefLoading(false);
    }
  };

  const generateBriefPdf = async () => {
    setBriefPdfGenerating(true);
    try {
      const result = await post(`/opportunities/${id}/executive-brief/pdf`);
      setBriefPdfUrl(result.download_url);
      toast.success('PDF generated successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setBriefPdfGenerating(false);
    }
  };

  const sendBriefEmail = async () => {
    if (!briefEmailTo || !briefPdfUrl) return;
    setSendingBrief(true);
    try {
      await post('/gmail/send', {
        to: briefEmailTo,
        subject: `Executive Deal Brief: ${opportunity.company_name}`,
        body: `Please find attached the Executive Deal Brief for ${opportunity.company_name}.`,
        attachments: [{
          id: `brief-${id}-${Date.now()}`,
          name: `Executive_Brief_${opportunity.company_name.replace(/\s+/g, '_')}.pdf`,
          url: briefPdfUrl,
          type: 'application/pdf'
        }]
      });
      toast.success('Brief sent successfully');
      setSendBriefOpen(false);
      setBriefEmailTo('');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingBrief(false);
    }
  };

  const getRiskSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (!opportunity) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="opportunity-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/pipeline')}
              className="rounded-sm"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl">{opportunity.company_name}</h1>
              <p className="text-muted-foreground">
                {opportunity.contact_name}
                {opportunity.contact_title ? <span className="opacity-70"> · {opportunity.contact_title}</span> : null}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Executive Brief Button - always visible */}
            <Button
              variant="outline"
              onClick={openExecutiveBrief}
              className="rounded-sm border-[#B89B5E]/50 text-[#B89B5E] hover:bg-[#B89B5E]/10 hover:border-[#B89B5E]"
              data-testid="executive-brief-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Executive Brief
            </Button>

            {/* Track Visit — only when a contact has been linked. Logs the
                visit against that contact regardless of which page the
                button is on, per the mileage spec. */}
            {opportunity.primary_contact_id && !isExecutiveViewer && (
              <Button
                variant="outline"
                onClick={() => setTrackVisitOpen(true)}
                className="rounded-sm"
                data-testid="track-visit-opp-btn"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Track Visit
              </Button>
            )}
            
            {!isExecutiveViewer && (
              <>
                {editMode ? (
                  <>
                    <Button variant="outline" onClick={() => setEditMode(false)} className="rounded-sm">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSave} className="rounded-sm" disabled={loading} data-testid="save-opp-btn">
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setEditMode(true)} className="rounded-sm" data-testid="edit-opp-btn">
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                
                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="rounded-sm" data-testid="delete-opp-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Opportunity</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this opportunity? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive" data-testid="confirm-delete-btn">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            )}
          </div>
        </div>

        {/* Stage Selector */}
        <Card className="rounded-sm border" data-testid="stage-selector">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              {STAGES.map((stage, index) => (
                <button
                  key={stage.id}
                  onClick={() => !isExecutiveViewer && handleStageChange(stage.id)}
                  disabled={isExecutiveViewer}
                  className={`flex-shrink-0 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                    opportunity.stage === stage.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  } ${isExecutiveViewer ? 'cursor-default' : 'cursor-pointer'}`}
                  data-testid={`stage-btn-${stage.id}`}
                >
                  {stage.name}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="details" className="space-y-4">
              <TabsList className="rounded-sm">
                <TabsTrigger value="details" className="rounded-sm" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="intelligence" className="rounded-sm" data-testid="tab-intelligence">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI Intelligence
                </TabsTrigger>
                <TabsTrigger value="timeline" className="rounded-sm" data-testid="tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="activity" className="rounded-sm" data-testid="tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="files" className="rounded-sm" data-testid="tab-files">Files</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <Card className="rounded-sm border">
                  <CardContent className="p-6 space-y-6">
                    {editMode ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Company Name</Label>
                          <Input
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Contact Name</Label>
                          <Input
                            value={formData.contact_name}
                            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Contact Title <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                          <Input
                            value={formData.contact_title || ''}
                            onChange={(e) => setFormData({ ...formData, contact_title: e.target.value })}
                            placeholder="e.g., Owner, GM, Director of Operations"
                            className="rounded-sm"
                            data-testid="opp-edit-contact-title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.contact_email}
                            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={formData.contact_phone || ''}
                            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Property Type</Label>
                          <Select
                            value={formData.property_type}
                            onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                          >
                            <SelectTrigger className="rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PROPERTY_TYPES.map(type => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Region</Label>
                          <Select
                            value={formData.region || ''}
                            onValueChange={(v) => setFormData({ ...formData, region: v })}
                          >
                            <SelectTrigger className="rounded-sm">
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {REGIONS.map(region => (
                                <SelectItem key={region} value={region}>{region}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Deal Value ($)</Label>
                          <Input
                            type="number"
                            value={formData.deal_value}
                            onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Probability (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.probability}
                            onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                            className="rounded-sm"
                          />
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={formData.description || ''}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={4}
                            className="rounded-sm resize-none"
                          />
                        </div>
                        
                        {/* Timing Context Fields - Edit Mode */}
                        <div className="col-span-2 border-t pt-4 mt-2">
                          <h4 className="text-sm font-medium mb-3">Timing & Budget Context</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label>Expected Close Month</Label>
                              <Input
                                type="month"
                                value={formData.expected_close_month || ''}
                                onChange={(e) => setFormData({ ...formData, expected_close_month: e.target.value })}
                                className="rounded-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Target Rollout Season</Label>
                              <Input
                                value={formData.target_rollout_season || ''}
                                onChange={(e) => setFormData({ ...formData, target_rollout_season: e.target.value })}
                                placeholder="e.g., Fall 2026"
                                className="rounded-sm"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Budget Cycle</Label>
                              <Select
                                value={formData.budget_cycle || ''}
                                onValueChange={(v) => setFormData({ ...formData, budget_cycle: v })}
                              >
                                <SelectTrigger className="rounded-sm">
                                  <SelectValue placeholder="Select quarter" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                                  <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                                  <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                                  <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Company</p>
                              <p className="font-medium">{opportunity.company_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Contact</p>
                              <p className="font-medium">{opportunity.contact_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Email</p>
                              <p className="font-medium">{opportunity.contact_email || 'Not available'}</p>
                            </div>
                          </div>
                          {(opportunity.contact_phone || businessEnrichment?.business_phone) && (
                            <div className="flex items-center gap-3">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Phone</p>
                                <a href={`tel:${opportunity.contact_phone || businessEnrichment?.business_phone}`} className="font-medium text-blue-600 hover:underline">
                                  {opportunity.contact_phone || businessEnrichment?.business_phone}
                                </a>
                                {businessEnrichment?.business_phone && !opportunity.contact_phone && (
                                  <span className="text-xs text-muted-foreground ml-2">(Business)</span>
                                )}
                              </div>
                            </div>
                          )}
                          {!opportunity.contact_phone && !businessEnrichment?.business_phone && (
                            <div className="flex items-center gap-3">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Phone</p>
                                <p className="text-sm text-muted-foreground">Not available</p>
                              </div>
                            </div>
                          )}
                          {/* Enrich Company Button - show when no phone/website available */}
                          {!businessEnrichment?.business_phone && !businessEnrichment?.business_website && !opportunity.contact_phone && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={enrichCompany}
                              disabled={enrichmentLoading}
                              className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                              {enrichmentLoading ? (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                                  Looking up...
                                </>
                              ) : (
                                <>
                                  <Search className="h-3 w-3 mr-2" />
                                  Find Business Phone & Website
                                </>
                              )}
                            </Button>
                          )}
                          {businessEnrichment?.business_website && (
                            <div className="flex items-center gap-3">
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Website</p>
                                <button 
                                  type="button"
                                  className="font-medium text-blue-600 hover:underline truncate block max-w-[200px] cursor-pointer text-left bg-transparent border-none p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const url = businessEnrichment.business_website.startsWith('http') 
                                      ? businessEnrichment.business_website 
                                      : `https://${businessEnrichment.business_website}`;
                                    const newWindow = window.open('', '_blank');
                                    if (newWindow) {
                                      newWindow.location.href = url;
                                    } else {
                                      window.location.href = url;
                                    }
                                  }}
                                >
                                  {businessEnrichment.business_website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Deal Value</p>
                              <p className="font-medium">{formatCurrency(opportunity.deal_value)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Percent className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Probability</p>
                              <p className="font-medium">{opportunity.probability}%</p>
                            </div>
                          </div>
                          {opportunity.region && (
                            <div className="flex items-center gap-3">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Region</p>
                                <p className="font-medium">{opportunity.region}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Property Type</p>
                              <p className="font-medium">{opportunity.property_type}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Timing Context Fields */}
                        {(opportunity.expected_close_month || opportunity.target_rollout_season || opportunity.budget_cycle) && (
                          <div className="col-span-2 pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Timing & Budget Context</p>
                            <div className="grid grid-cols-3 gap-4">
                              {opportunity.expected_close_month && (
                                <div>
                                  <p className="text-xs text-muted-foreground">Expected Close</p>
                                  <p className="font-medium">{new Date(opportunity.expected_close_month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</p>
                                </div>
                              )}
                              {opportunity.target_rollout_season && (
                                <div>
                                  <p className="text-xs text-muted-foreground">Target Rollout</p>
                                  <p className="font-medium">{opportunity.target_rollout_season}</p>
                                </div>
                              )}
                              {opportunity.budget_cycle && (
                                <div>
                                  <p className="text-xs text-muted-foreground">Budget Cycle</p>
                                  <p className="font-medium">{opportunity.budget_cycle}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {opportunity.description && (
                          <div className="col-span-2 pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Description</p>
                            <p className="text-sm">{opportunity.description}</p>
                          </div>
                        )}
                        
                        {/* Source & Location Info */}
                        {(opportunity.source || opportunity.location || opportunity.account_location) && (
                          <div className="col-span-2 pt-4 border-t border-border">
                            <div className="grid grid-cols-2 gap-4">
                              {opportunity.source && (
                                <div>
                                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Source</p>
                                  <Badge variant="outline" className="text-xs">{opportunity.source}</Badge>
                                </div>
                              )}
                              {(opportunity.location?.city || opportunity.account_location?.city) && (
                                <div className="flex items-start gap-2">
                                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                                  <div>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Location</p>
                                    <p className="font-medium text-sm">
                                      {[
                                        (opportunity.location?.city || opportunity.account_location?.city),
                                        (opportunity.location?.province_state || opportunity.account_location?.province_state),
                                        (opportunity.location?.country || opportunity.account_location?.country)
                                      ].filter(Boolean).join(', ')}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* AI Intelligence Tab */}
              <TabsContent value="intelligence">
                <div className="space-y-6">
                  {/* Document 2 · Phase 2 — Signal Story Card (read-only context) */}
                  <SignalStoryCard opportunityId={id} opportunity={opportunity} />

                  {/* Opportunity Momentum */}
                  <OpportunityMomentum opportunityId={id} />
                  
                  {/* Call Clarity Brief */}
                  <CallClarityBrief opportunityId={id} />
                  
                  {/* Vision Proposal Generator */}
                  <VisionProposalGenerator 
                    opportunityId={id} 
                    opportunityData={opportunity}
                  />

                  {/* Media Studio launcher — turn this opportunity into public-safe content */}
                  <OpportunityMediaLauncher opportunityId={id} opportunity={opportunity} />

                  {/* Document 2 · Phase 5 — AI Outreach Email Composer */}
                  <OutreachEmailComposer
                    opportunityId={id}
                    opportunity={opportunity}
                  />
                </div>
              </TabsContent>

              <TabsContent value="timeline">
                <OpportunityTimeline opportunityId={id} />
              </TabsContent>

              <TabsContent value="activity">
                <Card className="rounded-sm border">
                  <CardContent className="p-6 space-y-6">
                    {!isExecutiveViewer && (
                      <form onSubmit={handleAddActivity} className="space-y-4" data-testid="add-activity-form">
                        <div className="flex gap-2 flex-wrap">
                          {ACTIVITY_TYPES.map(type => (
                            <Button
                              key={type.id}
                              type="button"
                              variant={newActivity.type === type.id ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => {
                                if (type.id === 'email') {
                                  // Navigate to Conversations (emails) page with contact email pre-filled
                                  const contactEmail = opportunity?.contact_email || '';
                                  const contactName = opportunity?.contact_name || '';
                                  navigate(`/emails?to=${encodeURIComponent(contactEmail)}&name=${encodeURIComponent(contactName)}&opportunity_id=${id}`);
                                } else {
                                  setNewActivity({ ...newActivity, type: type.id });
                                }
                              }}
                              className="rounded-sm"
                            >
                              <type.icon className="h-3 w-3 mr-1" />
                              {type.name}
                            </Button>
                          ))}
                        </div>
                        <Textarea
                          value={newActivity.content}
                          onChange={(e) => setNewActivity({ ...newActivity, content: e.target.value })}
                          placeholder="Add a note, log a call, or record activity..."
                          rows={3}
                          className="rounded-sm resize-none"
                          data-testid="activity-content-input"
                        />
                        <Button type="submit" className="rounded-sm" disabled={loading || !newActivity.content.trim()} data-testid="add-activity-btn">
                          Add Activity
                        </Button>
                      </form>
                    )}

                    <div className="space-y-4">
                      {activities.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">No activity yet</p>
                      ) : (
                        activities.map((activity) => (
                          <div key={activity.id} className="flex gap-4 pb-4 border-b border-border last:border-0">
                            <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                              {activity.activity_type === 'note' && <FileText className="h-4 w-4" />}
                              {activity.activity_type === 'call' && <Phone className="h-4 w-4" />}
                              {activity.activity_type === 'email' && <Mail className="h-4 w-4" />}
                              {activity.activity_type === 'meeting' && <User className="h-4 w-4" />}
                              {activity.activity_type === 'stage_change' && <Clock className="h-4 w-4" />}
                              {activity.activity_type === 'created' && <Building2 className="h-4 w-4" />}
                              {activity.activity_type === 'assignment' && <User className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium text-sm">{activity.created_by_name}</p>
                                <p className="text-xs text-muted-foreground">{formatDateTime(activity.created_at)}</p>
                              </div>
                              <p className="text-sm text-muted-foreground">{activity.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="files">
                <Card className="rounded-sm border">
                  <CardContent className="p-6 space-y-4">
                    {!isExecutiveViewer && (
                      <div className="border-2 border-dashed border-border rounded-sm p-8 text-center">
                        <Upload className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-4">
                          Drag and drop files here, or click to browse
                        </p>
                        <input
                          type="file"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="file-upload"
                          data-testid="file-upload-input"
                        />
                        <label htmlFor="file-upload">
                          <Button asChild className="rounded-sm cursor-pointer">
                            <span>Choose File</span>
                          </Button>
                        </label>
                      </div>
                    )}

                    <div className="space-y-2">
                      {opportunity.attachments?.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">No files attached</p>
                      ) : (
                        opportunity.attachments?.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-3 bg-muted rounded-sm">
                            <div className="flex items-center gap-3">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{file.filename}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(file.uploaded_at)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <EngagementsSection scope="opportunity" entityId={id} entityName={opportunity.company_name} canEdit={!isExecutiveViewer} />

            <Card className="rounded-sm border">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Weighted Value</p>
                  <p className="text-xl font-serif">
                    {formatCurrency(opportunity.deal_value * (opportunity.probability / 100))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Stage</p>
                  <Badge variant="secondary" className="rounded-sm mt-1">
                    {STAGE_DISPLAY_NAMES[opportunity.stage]}
                  </Badge>
                </div>
                {opportunity.assigned_to_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned To</p>
                    <p className="font-medium">{opportunity.assigned_to_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Created</p>
                  <p className="text-sm">{formatDateTime(opportunity.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Updated</p>
                  <p className="text-sm">{formatDateTime(opportunity.updated_at)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Deal Intelligence Panel */}
            {intelligence && (
              <Card className="rounded-sm border border-[#C9A227]/30 bg-gradient-to-br from-[#C9A227]/5 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Brain className="h-4 w-4 text-[#C9A227]" />
                    Deal Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Probability */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Probability</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${
                        intelligence.probability?.band === 'very_high' ? 'text-green-600' :
                        intelligence.probability?.band === 'high' ? 'text-blue-600' :
                        intelligence.probability?.band === 'medium' ? 'text-yellow-600' :
                        'text-gray-500'
                      }`}>
                        {intelligence.summary?.probability_display}
                      </span>
                      <Badge variant="outline" className={`text-xs ${
                        intelligence.probability?.band === 'very_high' ? 'border-green-300 bg-green-50 text-green-700' :
                        intelligence.probability?.band === 'high' ? 'border-blue-300 bg-blue-50 text-blue-700' :
                        intelligence.probability?.band === 'medium' ? 'border-yellow-300 bg-yellow-50 text-yellow-700' :
                        'border-gray-300 bg-gray-50 text-gray-600'
                      }`}>
                        {intelligence.probability?.band?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  {/* Estimated Value */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Est. Value</span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#C9A227]">{intelligence.summary?.value_display}</p>
                      <p className="text-xs text-muted-foreground">{intelligence.summary?.value_range_display}</p>
                    </div>
                  </div>

                  {/* Close Window */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Close Window</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{intelligence.summary?.close_display}</p>
                      <p className="text-xs text-muted-foreground">{intelligence.close_window?.source}</p>
                    </div>
                  </div>

                  {/* Signal Strength */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Signal Strength</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        intelligence.signal_strength?.strength === 'very_high' ? 'bg-green-500' :
                        intelligence.signal_strength?.strength === 'high' ? 'bg-blue-500' :
                        intelligence.signal_strength?.strength === 'medium' ? 'bg-yellow-500' :
                        'bg-gray-400'
                      }`} />
                      <span className="text-sm font-medium">{intelligence.summary?.signal_display}</span>
                    </div>
                  </div>

                  {/* Decision Maker Coverage */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">DM Coverage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-xs ${
                        intelligence.decision_maker_coverage?.coverage_level === 'strong' ? 'border-green-300 bg-green-50 text-green-700' :
                        intelligence.decision_maker_coverage?.coverage_level === 'moderate' ? 'border-yellow-300 bg-yellow-50 text-yellow-700' :
                        'border-gray-300 bg-gray-50 text-gray-600'
                      }`}>
                        {intelligence.decision_maker_coverage?.count || 0} contacts
                      </Badge>
                    </div>
                  </div>

                  {/* Probability Factors */}
                  {intelligence.probability?.modifiers?.length > 0 && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Factors affecting probability:</p>
                      <div className="space-y-1">
                        {intelligence.probability.modifiers.map((mod, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{mod.factor}</span>
                            <span className={mod.impact.startsWith('+') ? 'text-green-600' : 'text-red-600'}>
                              {mod.impact}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Primary Outreach Target */}
                  {intelligence.primary_outreach_target && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                        <Target className="h-3 w-3" /> Primary Outreach Target
                      </p>
                      <div className="bg-muted/50 p-2 rounded">
                        <p className="font-medium text-sm">{intelligence.primary_outreach_target.name}</p>
                        <p className="text-xs text-muted-foreground">{intelligence.primary_outreach_target.title}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          Score: {intelligence.primary_outreach_target.decision_maker_score}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Suggested Decision Makers Panel */}
            <Card className="rounded-sm border border-border">
              <CardHeader className="pb-3 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Target className="h-4 w-4 text-[#B89B5E]" />
                      Suggested Decision Makers
                    </CardTitle>
                    {suggestedContacts.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {suggestedContacts.length} potential {suggestedContacts.length === 1 ? 'contact' : 'contacts'} found
                      </p>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={runDiscovery}
                    disabled={discoveryLoading}
                    className="h-8 text-xs rounded-sm border-[#B89B5E] text-[#B89B5E] hover:bg-[#B89B5E]/10"
                  >
                    {discoveryLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        Searching...
                      </>
                    ) : suggestedContacts.length > 0 ? (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Refresh
                      </>
                    ) : (
                      <>
                        <Search className="h-3 w-3 mr-1" />
                        Discover
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {suggestedContacts.length === 0 ? (
                  <div className="p-6 text-center" style={{ backgroundColor: '#F7F5F2' }}>
                    <div className="w-12 h-12 rounded-full bg-[#B89B5E]/10 flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-[#B89B5E]" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">No decision makers found yet</p>
                    <p className="text-xs text-muted-foreground">
                      Click "Discover" to search for contacts at {opportunity?.company_name || 'this company'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {suggestedContacts.map((contact) => (
                      <div 
                        key={contact.id} 
                        className={`p-4 transition-colors ${
                          contact.is_primary_target ? 'bg-[#B89B5E]/5' : 'hover:bg-muted/30'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-sm text-[#1C1C1C]">{contact.name || contact.title}</p>
                              {contact.is_primary_target && (
                                <Badge className="text-[10px] bg-[#B89B5E] text-white border-0 px-1.5 py-0">
                                  PRIMARY
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{contact.title}</p>
                            {contact.company_name && (
                              <p className="text-xs text-muted-foreground mb-2">{contact.company_name}</p>
                            )}
                            
                            {/* Reasoning/Approach */}
                            {(contact.reasoning || contact.outreach_approach) && (
                              <p className="text-xs text-muted-foreground italic mb-2 line-clamp-2">
                                {contact.reasoning || contact.outreach_approach}
                              </p>
                            )}
                            
                            {/* Badges */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={`text-[10px] rounded-sm ${
                                contact.confidence === 'high' ? 'border-green-400 text-green-700 bg-green-50' :
                                contact.confidence === 'medium' ? 'border-[#B89B5E] text-[#B89B5E] bg-[#B89B5E]/5' :
                                'border-gray-300 text-gray-600'
                              }`}>
                                {contact.confidence_score ? `${contact.confidence_score}%` : contact.confidence?.toUpperCase() || 'MEDIUM'}
                              </Badge>
                              {contact.decision_maker_score && (
                                <Badge variant="outline" className="text-[10px] rounded-sm border-gray-300">
                                  Score: {contact.decision_maker_score}
                                </Badge>
                              )}
                              {contact.source_type === 'serpapi_linkedin' && (
                                <Badge variant="outline" className="text-[10px] rounded-sm border-[#0077B5] text-[#0077B5] bg-[#0077B5]/5">
                                  LINKEDIN
                                </Badge>
                              )}
                              {contact.is_role_suggestion && (
                                <Badge variant="outline" className="text-[10px] rounded-sm border-blue-300 text-blue-600 bg-blue-50">
                                  ROLE SUGGESTION
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col gap-1.5">
                            <Button 
                              variant="outline"
                              size="sm" 
                              onClick={() => convertContact(contact.id, contact)}
                              className="h-7 text-xs rounded-sm border-green-400 text-green-700 hover:bg-green-50 px-2"
                              title="Add to Contacts"
                            >
                              <UserPlus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                            {contact.source_url && (
                              <a 
                                href={contact.source_url.startsWith('http') ? contact.source_url : `https://${contact.source_url}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-7 text-xs rounded-sm border border-border hover:bg-muted/50 px-2 text-muted-foreground"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                View
                              </a>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => dismissContact(contact.id)}
                              className="h-7 text-xs rounded-sm text-muted-foreground hover:text-red-600 hover:bg-red-50 px-2"
                              title="Dismiss"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Account Link */}
            {opportunity.account_id && (
              <Card className="rounded-sm border">
                <CardContent className="p-4">
                  <Link 
                    to={`/account/${opportunity.account_id}`}
                    className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-sm transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">View Account Details</span>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </Link>
              </CardContent>
            </Card>
            )}

            {/* Add Contact CTA when no contact exists */}
            {!opportunity.contact_name && !opportunity.primary_contact_id && (
              <Card className="rounded-sm border border-dashed border-[#C9A227]/50">
                <CardContent className="p-4">
                  <Link 
                    to={`/contacts?create=true&account_id=${opportunity.account_id || ''}&account_name=${encodeURIComponent(opportunity.company_name || '')}&opportunity_id=${opportunity.id}`}
                    className="flex items-center justify-between p-2 hover:bg-[#C9A227]/5 rounded-sm transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-[#C9A227]" />
                      <div>
                        <span className="text-sm font-medium text-[#C9A227]">Add Contact</span>
                        <p className="text-xs text-muted-foreground">No contact linked yet</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-[#C9A227] group-hover:scale-110 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            )}

            {opportunity.tags?.length > 0 && (
              <Card className="rounded-sm border">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {opportunity.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="rounded-sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Executive Brief Modal */}
      <Dialog open={briefModalOpen} onOpenChange={setBriefModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-serif text-[#1A1A1A]">
              <Sparkles className="w-5 h-5 text-[#B89B5E]" />
              Executive Deal Brief
            </DialogTitle>
            <DialogDescription>
              Strategic summary for {opportunity?.company_name}
            </DialogDescription>
          </DialogHeader>

          {briefLoading ? (
            <div className="py-12 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#B89B5E] mb-4" />
              <p className="text-muted-foreground">Generating executive brief...</p>
            </div>
          ) : brief ? (
            <div className="space-y-6 py-4">
              {/* Opportunity Snapshot */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-[#B89B5E] uppercase tracking-wider">
                  Opportunity Snapshot
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-[#B89B5E]/10 to-[#B89B5E]/5 border border-[#B89B5E]/20">
                    <p className="text-xs text-muted-foreground mb-1">Deal Value</p>
                    <p className="text-lg font-semibold text-[#1A1A1A]">{formatCurrency(brief.snapshot.deal_value)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                    <p className="text-xs text-muted-foreground mb-1">Stage</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">{brief.snapshot.stage_display}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                    <p className="text-xs text-muted-foreground mb-1">Probability</p>
                    <p className="text-lg font-semibold text-[#1A1A1A]">{brief.snapshot.probability}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30 border border-[#E8E6E1]">
                    <p className="text-xs text-muted-foreground mb-1">Weighted Value</p>
                    <p className="text-lg font-semibold text-emerald-600">{formatCurrency(brief.snapshot.weighted_value)}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {brief.snapshot.expected_close_window && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>Close: {brief.snapshot.expected_close_window}</span>
                    </div>
                  )}
                  {brief.snapshot.region && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{brief.snapshot.region}</span>
                    </div>
                  )}
                  {brief.snapshot.property_type && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      <span>{brief.snapshot.property_type}</span>
                    </div>
                  )}
                  {brief.snapshot.primary_contact && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="w-4 h-4" />
                      <span>{brief.snapshot.primary_contact.name}</span>
                    </div>
                  )}
                  {brief.snapshot.days_in_stage > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{brief.snapshot.days_in_stage} days in stage</span>
                    </div>
                  )}
                  {brief.snapshot.total_contacts > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>{brief.snapshot.total_contacts} contacts</span>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Opportunity Narrative */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-[#B89B5E] uppercase tracking-wider flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Opportunity Narrative
                </h3>
                <div className="p-4 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1] italic text-[#1A1A1A]">
                  {brief.narrative}
                </div>
              </div>

              {/* Risk Indicators */}
              {brief.risks && brief.risks.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#B89B5E] uppercase tracking-wider flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Risk Indicators
                  </h3>
                  <div className="space-y-2">
                    {brief.risks.map((risk, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center gap-3 p-3 rounded-lg ${getRiskSeverityColor(risk.severity)}`}
                      >
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{risk.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Next Step */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-[#B89B5E] uppercase tracking-wider flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Recommended Next Step
                </h3>
                <div className="p-4 rounded-lg bg-[#B89B5E] text-white">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="font-medium">{brief.recommended_next_step}</p>
                  </div>
                </div>
              </div>

              {/* Recent Activity Summary */}
              {brief.activity_summary?.recent_activities?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    Recent Activity
                  </h3>
                  <div className="space-y-2">
                    {brief.activity_summary.recent_activities.map((activity, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span className="capitalize">{activity.type}</span>
                        <span className="text-xs">{activity.date ? new Date(activity.date).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setBriefModalOpen(false)}>
              Close
            </Button>
            {brief && (
              <>
                {!briefPdfUrl ? (
                  <Button
                    onClick={generateBriefPdf}
                    disabled={briefPdfGenerating}
                    className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                    data-testid="generate-brief-pdf-btn"
                  >
                    {briefPdfGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Export PDF
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => window.open(briefPdfUrl, '_blank')}
                      data-testid="download-brief-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                    <Button
                      onClick={() => setSendBriefOpen(true)}
                      className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                      data-testid="send-brief-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send via Email
                    </Button>
                  </>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Brief Email Dialog */}
      <Dialog open={sendBriefOpen} onOpenChange={setSendBriefOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#B89B5E]" />
              Send Executive Brief
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={briefEmailTo}
                onChange={(e) => setBriefEmailTo(e.target.value)}
                data-testid="brief-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendBriefOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendBriefEmail}
              disabled={sendingBrief || !briefEmailTo}
              className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
              data-testid="send-brief-submit"
            >
              {sendingBrief ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Brief
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Track Visit dialog — opens when the user clicks Track Visit from the
          opportunity header. Logs against opportunity.primary_contact_id and
          carries the opportunity_id for cross-linking in reports. */}
      {opportunity?.primary_contact_id && (
        <TrackVisitDialog
          open={trackVisitOpen}
          onOpenChange={setTrackVisitOpen}
          contactId={opportunity.primary_contact_id}
          contactName={opportunity.contact_name}
          opportunityId={opportunity.id}
        />
      )}
    </Layout>
  );
}
